'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { MessageSquare, Send, Trash2, User } from 'lucide-react';
import type { FieldValue } from '../formFieldTypes';

/**
 * CommentsField — Lightweight comments field for dynamic forms
 *
 * Stores comments as an array within the form field value, so they become
 * part of the form submission payload (unlike CommentsSection which syncs
 * to the work-item comments API). This avoids silently creating comments
 * that are not part of the form submission.
 *
 * options.placeholder    — Placeholder text for the input
 * options.max_length     — Max characters per comment
 * options.allow_threads  — Reserved for future threaded comments
 */

type CommentsFieldOptions = {
      placeholder?: string;
      max_length?: number;
      allow_threads?: boolean;
      [key: string]: unknown;
};

interface CommentsFieldProps {
  field: {
    key: string;
    label: string;
    required?: boolean;
    readonly?: boolean;
    options?: unknown;
    help_text?: string;
    [key: string]: unknown;
  };
  value: FieldValue; // Array of { text, author?, timestamp }
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

interface CommentEntry {
  text: string;
  author?: string;
  timestamp: string;
  [key: string]: unknown;
}

export const CommentsField: React.FC<CommentsFieldProps> = ({ field, value, onChange, isDark }) => {
  const opts = (field.options ?? {}) as CommentsFieldOptions;
  const [draft, setDraft] = useState('');

  const comments = useMemo<CommentEntry[]>(() => {
    if (Array.isArray(value)) return value as CommentEntry[];
    if (value && typeof value === 'object') return [value as CommentEntry];
    return [];
  }, [value]);

  const maxLength = opts?.max_length ?? 2000;
  const placeholder = opts?.placeholder || 'Add a comment…';

  const addComment = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed || field.readonly) return;
    const entry: CommentEntry = {
      text: trimmed.slice(0, maxLength),
      timestamp: new Date().toISOString(),
    };
    onChange([...comments, entry]);
    setDraft('');
  }, [draft, comments, field.readonly, maxLength, onChange]);

  const removeComment = useCallback((index: number) => {
    if (field.readonly) return;
    onChange(comments.filter((_, i) => i !== index));
  }, [comments, field.readonly, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      addComment();
    }
  }, [addComment]);

  const inputClasses = `block w-full rounded-md px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-teal-500 resize-y ${
    isDark
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  }`;

  return (
    <div className="space-y-3">
      {/* Comments list */}
      {comments.length > 0 && (
        <div className="space-y-2">
          {comments.map((comment, idx) => (
            <div
              key={idx}
              className={`flex gap-2 rounded-lg p-2 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}
            >
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm whitespace-pre-wrap break-words ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {comment.text}
                </p>
                <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {new Date(comment.timestamp).toLocaleString()}
                </p>
              </div>
              {!field.readonly && (
                <button
                  type="button"
                  onClick={() => removeComment(idx)}
                  className="flex-shrink-0 p-1 text-red-500 hover:text-red-400 rounded"
                  title="Remove comment"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      {!field.readonly && (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={2}
            className={inputClasses}
            disabled={field.readonly}
          />
          <div className="flex items-center justify-between">
            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {draft.length}/{maxLength} • Ctrl+Enter to add
            </span>
            <button
              type="button"
              onClick={addComment}
              disabled={!draft.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              Add
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {comments.length === 0 && field.readonly && (
        <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <MessageSquare className="w-4 h-4" />
          No comments
        </div>
      )}
    </div>
  );
};

export default CommentsField;
